import React, { useState } from 'react'
import { Row, Col, Card, Typography, Button, Space, message, Modal, Form, Input, Select, DatePicker } from 'antd'
import { PlusOutlined, ReloadOutlined, DragOutlined } from '@ant-design/icons'
import TaskCard from './TaskCard'
import TaskCommentsModal from './TaskCommentsModal'
import { useProjectTasks } from '../../hooks/useProjectTasks'
import TaskModal from './TaskModal'

const { Title } = Typography
const { Option } = Select

const KanbanBoard = ({ projectId, projectInfo }) => {
  const [draggedTask, setDraggedTask] = useState(null)
  const [editingTask, setEditingTask] = useState(null)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [showCommentsModal, setShowCommentsModal] = useState(false)

  const { kanbanData, loading, error, refreshTasks, updateTaskStatus, deleteTask, canCreate, canUpdate, canDelete } = useProjectTasks(projectId, { autoFetch: true })

  const statuses = [
    { key: 'todo', title: 'งานที่ต้องทำ', color: '#f0f0f0', icon: '📋' },
    { key: 'in_progress', title: 'กำลังดำเนินการ', color: '#e6f7ff', icon: '⚡' },
    { key: 'testing', title: 'ทดสอบ', color: '#fff7e6', icon: '🧪' },
    { key: 'done', title: 'เสร็จสิ้น', color: '#f6ffed', icon: '✅' }
  ]

  // Drag & Drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = async (e, newStatus) => {
    e.preventDefault()
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null)
      return
    }

    const result = await updateTaskStatus(draggedTask.id, newStatus)
    if (result.success) message.success('อัปเดตสถานะงานสำเร็จ')
    else message.error(result.message || 'ไม่สามารถอัปเดตสถานะงานได้')

    setDraggedTask(null)
  }

  // Task handlers
  const handleCreateTask = () => { setEditingTask(null); setShowTaskModal(true) }
  const handleEditTask = (task) => { 
    console.log('task', task);
    setEditingTask(task); 
    setShowTaskModal(true);
   }
  const handleDeleteTask = async (task) => {
    const result = await deleteTask(task.id)
    if (result.success) message.success('ลบงานสำเร็จ')
    else message.error(result.message || 'ไม่สามารถลบงานได้')
  }
  const handleViewComments = (task) => { setSelectedTask(task); setShowCommentsModal(true) }

  if (loading) return <div style={{ padding: 50, textAlign: 'center' }}>กำลังโหลดข้อมูล...</div>
  if (error) return <div style={{ padding: 50, textAlign: 'center' }}>เกิดข้อผิดพลาด: {error}</div>
  if (!kanbanData) return null

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, padding: '12px 16px', backgroundColor: '#fff', borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}><DragOutlined /> Kanban Board - {projectInfo?.project_name}</Title>
          {projectInfo?.project_code && <p style={{ margin: 0, color: '#666', fontSize: 12 }}>{projectInfo.project_code}</p>}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refreshTasks} loading={loading}>รีเฟรช</Button>
          {canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTask}>เพิ่มงาน</Button>}
        </Space>
      </div>

      {/* Kanban Columns */}
      <Row gutter={16}>
        {statuses.map(status => {
          const tasks = kanbanData.kanban_board?.[status.key] || []

          return (
            <Col span={6} key={status.key}>
              <Card
                title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>{status.icon}</span> <span>{status.title}</span> <span style={{ backgroundColor: '#f0f0f0', borderRadius: 12, padding: '2px 8px', fontSize: 12, color: '#666' }}>{tasks.length}</span></div>}
                size="small"
                style={{ minHeight: '60vh', backgroundColor: status.color }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status.key)}
                bodyStyle={{ padding: 8, overflowY: 'auto', maxHeight: '70vh' }}
              >
                {tasks.length > 0 ? tasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={canUpdate ? handleEditTask : undefined}
                    onDelete={canDelete ? handleDeleteTask : undefined}
                    onViewComments={handleViewComments}
                    onDragStart={handleDragStart}
                    isDragging={draggedTask?.id === task.id}
                  ></TaskCard>
                  
                )) : <div style={{ textAlign: 'center', color: '#999', padding: 20, fontSize: 12 }}>ไม่มีงานในสถานะนี้</div>}
              </Card>
            </Col>
          )
        })}
      </Row>

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          visible={showTaskModal}
          task={editingTask}
          projectId={projectId}
          onClose={() => setShowTaskModal(false)}
          onSuccess={() => {
            setShowTaskModal(false)
            refreshTasks()
          }}
        />
      )}

      {/* Comments Modal */}
      {showCommentsModal && selectedTask && (
        <TaskCommentsModal
          visible={showCommentsModal}
          task={selectedTask}
          canCreateComments={true}
          onClose={() => setShowCommentsModal(false)}
        />
      )}
    </div>
  )
}

export default KanbanBoard
